<template>
  <div class="animated fadeIn">
    <div class="container">
      <clientselection v-if="isMSMUser || hasDashboardCustomerSelection()" :disabled="appLoading" v-on:clientselectionchanged="onClientSelectionChanged"/>
      <div class="row">
        <div class="col-xl">
          <div ref="cockpit-and-filter" class="card">
            <div class="card-header app-ctrl" >
              <b-row>
                <div class="mb-3 col-md">
                    <treeselect :placeholder="$t('Webportal.ProjectFilter.Placeholder')"
                                ref="projectTreeselect"
                                instance-id="projects"
                                v-model="selection.surveys"
                                :options="filterValues.projects"
                                :auto-load-root-options="false"
                                :multiple="true"
                                :max-height="600"
                                :disable-branch-nodes="true"
                                :searchable="true"
                                :clearable="true"
                                :disableFuzzyMatching="true"
                                :showCount="true"
                                :flat="false"
                                showCountOf="LEAF_DESCENDANTS"
                                :disabled="appLoading || !clientSelectionCompleted"
                                :limit="4"
                                :limit-text="limitText"
                                @close="handleProjectClose"
                                @update:model-value="handleTreeselectInput" />
                </div>
                <div class="mb-3 col-md">
                    <treeselect :placeholder="$t('Webportal.WaveFilter.Placeholder')"
                                ref="waveTreeselect"
                                instance-id="waves"
                                v-model="selection.waves"
                                :options="filterValues.waves"
                                :auto-load-root-options="false"
                                :multiple="true"
                                :searchable="true"
                                :disableFuzzyMatching="true"
                                :showCount="true"
                                :flat="true"
                                showCountOf="LEAF_DESCENDANTS"
                                valueConsistsOf="LEAF_PRIORITY"
                                :disable-branch-nodes="true"
                                :limit="4"
                                :limit-text="limitText"
                                @close="handleWaveClose"
                                @update:model-value="handleTreeselectInput"
                                :disabled="!isWaveFilterEnabled || appLoading || !clientSelectionCompleted" />
                  </div>
                <div class="mb-3 col-md">
                    <treeselect :placeholder="$t('Webportal.ScenarioFilter.Placeholder')"
                                ref="scenarioTreeselect"
                                instance-id="scenario"
                                v-model="selection.scenarios"
                                :allow-clearing-disabled="true"
                                :options="filterValues.scenarios"
                                :auto-load-root-options="false"
                                :multiple="true"
                                :searchable="true"
                                :disableFuzzyMatching="true"
                                :showCount="true"
                                :flat="true"
                                showCountOf="LEAF_DESCENDANTS"
                                :limit="4"
                                :limit-text="limitText"
                                @close="handleScenarioClose"
                                @update:model-value="handleTreeselectInput"
                                :disabled="!isScenarioFilterEnabled || appLoading || !clientSelectionCompleted" />
                  </div>
                <div class="mb-3 col-md" v-if="isDateRangeFilterEnabled">
                  <date-range-picker  ref="dateRangePicker"
                                      class=""
                                      :date-range="selection.dateRange"
                                      :locale-data="localeData"
                                      :ranges="false"
                                      :time-picker="false"
                                      :auto-apply="true"
                                      :show-week-numbers="true"
                                      :show-dropdowns="true"
                                      :disabled="appLoading || !clientSelectionCompleted"
                                      opens="left"
                                      @toggle="toggleDateRange"
                                      @update="handleDateRangeClose">
                    <template #input="picker" class="d-flex align-items-center justify-content-between w-100">
                      <font-awesome-icon :icon="['fas', 'calendar']" class="mr-1" />
                      <span v-show="picker.startDate">
                        {{ msm_date(picker.startDate) }} - {{ msm_date(picker.endDate) }}
                      </span>
                      <span class="placeholder" v-show="picker.startDate == null">
                        {{ $t('Webportal.DateRangeFilter.Placeholder') }}
                      </span>
                      <b-button class="btn-close" v-show="picker.startDate" @click.stop="clearDateRange()"></b-button>
                    </template>
                    <template #date="data">
                      <div>{{ data.date.getDate() }}</div>
                      <div class="check-count">
                        <span v-if="getCheckCount(data.date)" class="badge badge-secondary">n={{ getCheckCount(data.date) }}</span>
                        <span v-else>&nbsp;</span>
                      </div>
                    </template>
                  </date-range-picker>
                </div>
              </b-row>
              <b-row>
                <div class="mb-3 col-md"
                     v-for="(cust, index) in customProperties" :key="index">
                  <treeselect :placeholder="cust.label"
                              :ref="getRefName(cust)"
                              :instance-id="cust.id"
                              v-model="selection.customProperties[index]"
                              :allow-clearing-disabled="true"
                              :options="cust.children"
                              :auto-load-root-options="false"
                              :multiple="true"
                              :searchable="true"
                              :disableFuzzyMatching="true"
                              :showCount="true"
                              :flat="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :disabled="appLoading || !clientSelectionCompleted || checkGroupmodeTreeselectDisabled(index)"
                              :disable-branch-nodes="true"
                              :limit="4"
                              :limit-text="limitText"
                              @close="handleCloseCustom"
                              @update:model-value="handleTreeselectInput" />
                </div>
                <div class="mb-3 col-md">
                  <b-tooltip :disabled.sync="isLocationTreeselectTooltipDisabled"
                              :show.sync="isLocationTreeselectTooltipShown"
                              triggers="click blur"
                              target="locationTreeselect">
                    {{ $t('Webportal.Dashboard.TooManyClientLocations') }}
                  </b-tooltip>
                  <treeselect :placeholder="$t('Webportal.LocationFilter.Placeholder')"
                              ref="locationTreeselect"
                              id="locationTreeselect"
                              instance-id="location"
                              v-model="selection.locations"
                              :options="clientLocations"
                              :auto-load-root-options="false"
                              :multiple="true"
                              :searchable="true"
                              :disableFuzzyMatching="true"
                              :showCount="true"
                              :flat="true"
                              showCountOf="LEAF_DESCENDANTS"
                              :limit="4"
                              :limit-text="limitText"
                              @close="handleLocationClose"
                              @update:model-value="handleTreeselectInput"
                              :disabled="!isLocationFilterEnabled || appLoading || !clientSelectionCompleted || checkGroupmodeTreeselectDisabled(-1)" />
                </div>
              </b-row>

              <div id="widgetRow" class="row" style="align-items: center;">
                <div class="col-md">
                  <div class="row" style="align-items: center;">
                    <div v-if="getWidgetIsActive(Widget.TotalScore)" class="col-md-auto">
                      <WidgetTotalScore
                        :widgetdata="widgetTotalScoreData"
                        :thresholds="thresholds"
                        :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
                        @close-widget="widgetContextMenuClicked(widgetMenuItems[Widget.TotalScore])" />
                    </div>
                    <div v-if="getWidgetIsActive(Widget.Project)" class="col-md-auto">
                      <WidgetCheckStatus
                        :widgetdata="widgetCheckStatusData"
                        :thresholds="thresholds"
                        @close-widget="widgetContextMenuClicked(widgetMenuItems[Widget.Project])" />
                    </div>
                    <div v-if="getWidgetIsActive(Widget.CheckRevisions)" class="col-md-auto">
                      <WidgetCheckRevisions
                        :widgetdata="widgetCheckRevisionsData"
                        :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
                        @close-widget="widgetContextMenuClicked(widgetMenuItems[Widget.CheckRevisions])" />
                    </div>
                    <div v-if="getWidgetIsActive(Widget.Kpi)" class="col-md-auto">
                      <WidgetKpi
                        :widgetdata="widgetKPIData"
                        :thresholds="thresholds"
                        :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
                        @close-widget="widgetContextMenuClicked(widgetMenuItems[Widget.Kpi])" />
                    </div>
                    <div v-show="areThresholdsAmbiguous && clientSettings.ShowScoreThresholdsSelection" class="col-md-auto">
                      <div class="mb-3" style="max-width:300px;">
                        <label for="ThresholdSelect">
                          {{ $t('Webportal.Administration.ScoreThresholds') }}
                          <span v-b-tooltip="{ title: $t('Webportal.ScoreThresholdsSelection.Description'), delay: toolTipDelay, triggers: 'hover' }">
                            <font-awesome-icon class="text-danger" :icon="['fas', 'info-circle']" />
                          </span>
                        </label>
                        <b-form-select id="ThresholdSelect"
                          v-model="selectedThresholdSurveyId"
                          size="sm"
                          :disabled="!clientSelectionCompleted || showPlaceholder || appLoading">
                          <b-form-select-option-group :label="$t('Webportal.Customer')">
                            <b-form-select-option v-if="clientSettings" :value="null">{{ clientSettings.ClientName }}</b-form-select-option>
                          </b-form-select-option-group>
                          <b-form-select-option-group v-if="surveyThresholds.length > 0" :label="$t('Webportal.Survey')">
                            <b-form-select-option
                              v-for="surveyThreshold in surveyThresholds"
                              :key="surveyThreshold.id"
                              :value="surveyThreshold.id">
                              {{ surveyThreshold.label }}
                            </b-form-select-option>
                          </b-form-select-option-group>
                        </b-form-select>
                      </div>
                    </div>
                    <div class="col-md-auto align-self-start" style="margin-top: 3px;">
                      <b-dropdown size="sm" text="Widgets" variant="secondary" :disabled="showPlaceholder || appLoading">
                        <b-dropdown-item v-for="wmi in widgetMenuItems.filter(wmi => wmi.isEnabled)"
                            :key="wmi.index"
                            @click="widgetContextMenuClicked(wmi)">
                              <font-awesome-icon v-if="wmi.active" icon="check" />
                              <span v-if="!wmi.active" style="display: inline-block; min-width: 14px;"/>
                              {{ wmi.text }}
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </div>
                </div>

                <div class="col-md-auto align-self-end">
                  <div class="flex-container cx-group-switch group-switch space-left">
                    <div for="CompareSwitch">
                      {{ $t('Webportal.Dashboard.CompareMode') }}
                      <span v-b-tooltip="{ title: $t('Webportal.Dashboard.CompareMode.Description'), delay: toolTipDelay, triggers: 'hover' }">
                        <font-awesome-icon :icon="['fas', 'info-circle']" />
                      </span>
                    </div>
                    <label class="switch switch-label switch-secondary space-left">
                      <input class="switch-input" type="checkbox"
                            v-model="CompareSwitch"
                            :disabled="appLoading || !clientSelectionCompleted || tabIndex !== 0"
                            @change="onCompareSwitchChanged">
                      <!-- v-validate data-vv-name="CompareSwitch" -->
                      <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
                    </label>
                  </div>
                  <div v-if="canChangeKpiMode"
                       class="flex-container cx-group-switch group-switch space-left">
                    <div for="KpiSwitch">
                      {{ $t('Webportal.Dashboard.KpiMode') }}
                      <span v-b-tooltip="{ title: $t('Webportal.Dashboard.KpiMode.Description'), delay: toolTipDelay, triggers: 'hover' }">
                        <font-awesome-icon :icon="['fas', 'info-circle']" />
                      </span>
                    </div>
                    <label class="switch switch-label switch-secondary space-left">
                      <input class="switch-input" type="checkbox"
                            v-model="KpiSwitch"
                            :disabled="appLoading || !isKpiAvailable || !clientSelectionCompleted || tabIndex > 1">
                            <!-- v-validate data-vv-name="KpiSwitch" -->
                            <!-- @change="onKpiSwitchChanged"> -->
                      <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
                    </label>
                  </div>
                </div>
                <div class="col-md-auto align-self-end">
                  <exportelementbutton elementRef="cockpit" right
                                      :parent="$refs"
                                      :additionalElements="[{ Name: $t('Webportal.Dashboard.Export.IncludeFilter'),
                                                              ElementRef: 'cockpit-and-filter' }]"
                                      fileName="Cockpit"
                                      :disabled="showPlaceholder || appLoading || !clientSelectionCompleted" />
                </div>
              </div>
            </div>

            <div ref="cockpit" class="app-ctrl">
              <b-card no-body>
                <b-tabs small card
                        v-if="clientSelectionCompleted"
                        v-model="tabIndex"
                        class="app-ctrl"
                        @input="onTabChanged">
                  <b-tab lazy v-if="this.dashboardtabs.EnableDashboardGrid" :title="$t('Webportal.Dashboard.AnalysisGrid')" active>
                    <div>
                      <dashboardpanel
                        :thresholds="thresholds"
                        :isCluster="!KpiSwitch"
                        :isCompareMode="CompareSwitch"
                        :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
                        :componentKey="componentKey"
                        :filtervalues="filterValues"
                        :selection="currentFilter" />
                        <!-- :groupmodeIndexIndicator="getGroupmodeIndexIndicator()" />
                        :activeCompareLabel="activeCompareLabel" /> -->
                    </div>
                  </b-tab>
                  <b-tab lazy v-if="this.dashboardtabs.EnableDashboardRanking" title="Ranking">
                    <div>
                      <dashboardpanelranking
                        :thresholds="thresholds"
                        :isCluster="!KpiSwitch"
                        :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
                        :filtervalues="filterValues"
                        :selection="currentFilter"/>
                    </div>
                  </b-tab>
                  <b-tab lazy v-if="this.dashboardtabs.EnableDashboardFunnel" title="Funnel" style="padding:0px;background:#e4e5e6;margin-top:-2px;">
                    <div>
                      <dashboardpanelfunnel
                        :percentageNumberOfDecimalPoints="percentageNumberOfDecimalPoints"
                        :filtervalues="filterValues"
                        :selection="currentFilter" />
                    </div>
                  </b-tab>
                  <!-- <b-tab title="Geomap">
                   <div>
                      <dashboardpanelgeomap v-if="tabIndex === 2" :geodata="geodata" />
                    </div>
                  </b-tab> -->
                </b-tabs>
              </b-card>
            </div>
          </div>

        </div>
      </div>
    </div>

    <b-modal ref="modalTooMuchGroup" id="modal-center" centered :title="$t('Webportal.Dashboard.Dialog.MaxWaves.Title')">
      <p class="my-4">{{ $t('Webportal.Dashboard.Dialog.MaxWaves.Text') }}</p>
      <div slot="modal-footer" class="w-100">
        <b-btn size="sm" class="float-end" variant="primary" @click="closeInfoDialog">
          {{ $t('Webportal.Dashboard.Ok') }}
        </b-btn>
      </div>
    </b-modal>
  </div>

</template>


<script>
  import axios from 'axios'
  import AppConfig from '@/mixins/AppConfig-APP_TARGET';
  import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";
  import EventBus from '@/services/EventBus.js'
  import AuthenticationService from '@/services/AuthenticationService';
  import DashboardService from '@/services/DashboardService'
  import AdministrationService from '@/services/AdministrationService'
  import dashboardpanel from '@/components/Dashboard/DashboardPanel'
  import dashboardpanelranking from '@/components/Dashboard/DashboardPanelRanking'
  import dashboardpanelfunnel from './Dashboard/DashboardPanelFunnel'
  import dashboardpanelgeomap from './Dashboard/DashboardPanelGeoMap'
  import WidgetTotalScore from '@/components/Dashboard/WidgetTotalScore'
  import WidgetCheckStatus from '@/components/Dashboard/WidgetCheckStatus'
  import WidgetCheckRevisions from '@/components/Dashboard/WidgetCheckRevisions'
  import WidgetKpi from '@/components/Dashboard/WidgetKpi'
  import exportelementbutton from './ExportElementButton'
  import clientselection from './ClientSelection'
  import DateRangePicker from 'vue3-daterange-picker'
  import moment from "moment";

  import CustomFilter from '@/plugins/CustomFilter';
import { mapGetters, mapActions } from 'vuex';

  const Widget = {
    TotalScore: 0,
    Project: 1,
    CheckRevisions: 2,
    Kpi: 3
  }

  export default {
    name: "Dashboard",

    mixins: [ AppConfig, SurveyRevisionMixin ],

    components:{
      clientselection,
      dashboardpanel,
      dashboardpanelranking,
      dashboardpanelfunnel,
      dashboardpanelgeomap,
      WidgetTotalScore,
      WidgetCheckStatus,
      WidgetCheckRevisions,
      WidgetKpi,
      exportelementbutton,
      DateRangePicker
    },

    data() {
      return {
        clientSelectionCompleted: false,
        filterValues: {
          clients: [], // not used for generic reporting
          projects: [],
          surveys: [],
          waves: [],
          scenarios: [],
          checkCounts: Map,
          locations: [],
          customProperties: [],
          customPropertiesEnabled: []
        },

        selection: {
          client: null,
          projects: [],
          surveys: [],
          waves: [],
          scenarios: [],
          dateRange: { startDate: null, endDate: null },
          locations: [],
          customProperties: []
        },

        Widget: Widget,
        widgetMenuItems: [
          { index: Widget.TotalScore, text: 'Total score', active: true, isEnabled: this.isDashboardTotalScoreWidgetEnabled() },
          { index: Widget.Project, text: 'Project', active: true, isEnabled: true },
          { index: Widget.CheckRevisions, text: this.$t("Webportal.Actions.SurveyRevisions"), active: true, isEnabled: this.userHasActionRevisePermission() },
          { index: Widget.Kpi, text: this.$t("Webportal.Kpi"), active: true, isEnabled: true },
        ],

        widgetTotalScoreData: null,
        widgetCheckStatusData: null,
        widgetCheckRevisionsData: null,
        widgetKPIData: null,

        currentFilter: null,

        isKpiAvailable: false,
        KpiSwitch: this.isKpiDefaultMode(),
        CompareSwitch: false,
        maxAllowedGroupSelections: 3,
        activeCompareLabel: '',

        optionsCancelToken: null,
        widgetCancelToken: null,

        tabIndex: 0,
        componentKey: 0,

        toolTipDelay: { delay: { "show": 500, "hide": 100 } },
        isLocationTreeselectTooltipDisabled: false,
        isLocationTreeselectTooltipShown: false,

        clientSettings: null,
        selectedThresholdSurveyId: null,
        percentageNumberOfDecimalPoints : 2,

        dashboardtabs: {
          EnableDashboardGrid: true,
          EnableDashboardFunnel: false,
          EnableDashboardRanking: false,
				  EnableDashboardGeomap: false,
				  EnableDashboardScorecard: false
        },

        geodata: {
            "Germany": { score: 98, color: "#4899ab"},
            "United Kingdom": { score: 85, color: "#c8b795" },
            "Switzerland": { score: 46, color: "#005470" },
            "France": { score: 50, color: "#6d6e70" },
            "Kazakhstan": { score: 76, color: "#c8b795"},
            "Ukraine": { score: 36, color: "#005470"},
            "Slovenia": { score: 75, color: "#c8b795"},
            "Belarus": { score: 93, color: "#4899ab"},
        },
      }
    },

    computed: {
      ...mapGetters('clientData', [ 'isClientReady', 'appLoading' ]),

      localeData: function() {
        return {
          format: moment.localeData().longDateFormat('L').toLowerCase(),
          weekLabel: this.$t('Webportal.CalendarWeekShort'),
          daysOfWeek: moment.localeData().weekdaysShort(),
          monthNames: moment.localeData().months(),
          firstDay: 1
        };
      },

      customProperties: function () {
        // Ensure that in compare mode all property values are enabled
        return !this.CompareSwitch ? this.filterValues.customProperties : this.filterValues.customPropertiesEnabled;
      },

      clientLocations: function() {
        if (this.isLocationFilterEnabled && this.filterValues.locations.length <= 1000) {
          this.isLocationTreeselectTooltipDisabled = true;
          this.isLocationTreeselectTooltipShown = false;
          return this.filterValues.locations;
        } else {
          this.isLocationTreeselectTooltipDisabled = false;
          return [];
        }
      },

      isMSMUser: function () {
        return AuthenticationService.isMSMUser;
      },

      localUserProfile: function () {
        return AuthenticationService.localUserProfile;
      },

      canChangeKpiMode: function() {
        return this.localUserProfile.Permissions.includes('Dashboard.CanChangeKpiMode');
      },

      showPlaceholder: function() {
        return !this.selection.projects || !this.selection.surveys;
      },

      isWaveFilterEnabled: function() {
        return this.filterValues && this.filterValues.waves && this.filterValues.waves.length > 0;
      },

      isScenarioFilterEnabled: function() {
        return this.filterValues && this.filterValues.scenarios && this.filterValues.scenarios.length > 0;
      },

      isDateRangeFilterEnabled: function() {
        return this.clientSettings && this.clientSettings.DateRangeFilterMode > 0;
      },

      isLocationFilterEnabled: function() {
        return this.filterValues && this.filterValues.locations && this.filterValues.locations.length > 0;
      },

      selectedSurveyIds: function() {
        return this.currentFilter?.surveys?.split(',').map(s => Number(s)) ?? [];
      },

      thresholds: function() {
        return this.selectedThresholdSurveyId ?
          this.clientSettings?.SurveyThresholds.filter(t => t.SurveyId == this.selectedThresholdSurveyId) :
          this.clientSettings?.Thresholds;
      },

      areThresholdsAmbiguous: function() {
        return this.selectedSurveyIds.length > 1 && this.surveyThresholds.length > 0;
      },

      surveyThresholds: function() {
        return _(this.clientSettings?.SurveyThresholds)
                .filter(t => this.selectedSurveyIds.includes(t.SurveyId))
                .groupBy(t => t.SurveyId)
                .map((items, key) => ({
                  id: Number(key),
                  label: this.filterValues?.surveys?.find(f => f.id == Number(key))?.label
                }))
                .value();
      }
    },

    watch: {
      currentFilter(newVal, oldVal) {
        if (!oldVal || this.checkFilterChanged(oldVal)) {
          //console.log('Dashboard - watch - currentFilter', JSON.stringify(oldVal), JSON.stringify(newVal));
          if (newVal.surveys)
          {
            this.getfilterValues();
            this.getDashboardWidgetData();
          }
        }
      },

      surveyThresholds() {
        // Preselect survey threshold if only one survey selected
        if (this.selectedSurveyIds.length == 1)
          this.selectedThresholdSurveyId = this.surveyThresholds.find(t => t.id == this.selectedSurveyIds[0])?.id ?? null;
        // Remove survey threshold selection when not in list any more or more than one survey is selected
        else if (this.areThresholdsAmbiguous || !this.surveyThresholds.find(t => t.id == this.selectedThresholdSurveyId))
          this.selectedThresholdSurveyId = null;
      }
    },

    created() {
      this.initializeTheme();
      // console.log('Dashboard - created');

      if (!this.isMSMUser) {
        this.clientSelectionCompleted = true;
        this.getfilterValues(true);
      }

      EventBus.$on('LocalizationChanged', this.localizationChanged);
    },

    beforeUnmount() {
      // console.log('Dashboard - beforeUnmount');
      EventBus.$off('LocalizationChanged', this.localizationChanged);
    },

    methods: {
      ...mapActions('clientData', [ 'setAppLoading' ]),
      msm_date(val) {
        return CustomFilter.msm_date(val)
      },

      getCheckCount(date) {
        return this.filterValues?.checkCountMap?.get(moment(date).format('YYYY-MM-DD'));
      },

      clearDateRange() {
        this.selection.dateRange.startDate = null;
        this.selection.dateRange.endDate = null;

        this.handleDateRangeClose();
      },

      toggleDateRange(show, event) {
        if (show && this.selection.dateRange.startDate == null && this.filterValues.maxCheckDate)
          // ensure to show latest check date on the left date range picker
          this.$nextTick(() => {
            this.$refs.dateRangePicker.changeRightMonth(
              { year: this.filterValues.maxCheckDate.year(),
                month: this.filterValues.maxCheckDate.month() + 1 })
          });
      },

      // ***********************************************************
      // HTML Events
      // ***********************************************************

      // Client Selection (for MSMUser)
      // ******************************
      onClientSelectionChanged(clientSettings) {
        try {
          if (clientSettings) {
            // console.log('Dashboard - onClientSelectionChanged - Settings received: ' + JSON.stringify(clientSettings));
            this.selection.client = clientSettings.ClientId ? clientSettings.ClientId : null;

            this.getClientSettings(this.selection.client);
          }
        } finally {
          this.getfilterValues(true);
          this.clientSelectionCompleted = true;
        }
      },

      // Projects Treeselect
      // *******************
      handleProjectClose(value, instanceId) {
        //console.log('Dashboard - handleProjectClose');

        if (this.checkFilterChanged()) {
          this.selectWaves();
          this.currentFilter = this.getCurrentFilterFromSelection();
        }
      },

      // Wave Treeselect
      // ***************
      handleWaveClose(value, instanceId) {
        // console.log('Dashboard - handleWaveClose');

        if (this.checkFilterChanged()) {
          this.currentFilter = this.getCurrentFilterFromSelection();
        }
      },

      // Scenario Treeselect
      // *******************
      handleScenarioClose(value, instanceId) {
        // console.log('Dashboard - handleScenarioClose');

        if (this.checkFilterChanged()) {
          this.currentFilter = this.getCurrentFilterFromSelection();
        }
      },

      handleDateRangeClose() {
        // console.log('Dashboard - handleDateRangeClose');

        if (this.checkFilterChanged()) {
          this.currentFilter = this.getCurrentFilterFromSelection();
        }
      },

      // Location Treeselect
      // *******************
      handleLocationClose(value, instanceId) {
        // console.log('Dashboard - handleLocationClose');

        if (this.CompareSwitch) {
          this.checkTooMuchSelectionsForGroupmode();
        }

        if (this.checkFilterChanged()) {
          this.currentFilter = this.getCurrentFilterFromSelection();
        }
      },

      // Generic Treeselect
      // ******************
      handleCloseCustom(value, instanceId) {
        // console.log('Dashboard - handleCloseCustom');

        setTimeout(f => {
          if (this.CompareSwitch) {
            this.checkTooMuchSelectionsForGroupmode();
          }

          if (this.checkFilterChanged()) {
            this.currentFilter = this.getCurrentFilterFromSelection();
          }
        }, 1);
      },

      handleTreeselectInput(value) {
        //console.log('Dashboard - handleTreeselectInput', value);

        // Workaround: Only call data loading when clear all is pressed
        if (value.length === 0 && this.checkFilterChanged() && this.clientSelectionCompleted) {
          this.currentFilter = this.getCurrentFilterFromSelection();
        }
      },

      // Widgets
      // *******
      widgetContextMenuClicked(e) {
        // console.log('Dashboard - widgetContextMenuClicked');
        e.active = !e.active;
      },

      // CompareSwitch
      // *************
      onCompareSwitchChanged(e) {
        // console.log('Dashboard - onCompareSwitchChanged');

        // clear selections
        this.selection.locations = [];
        for (let i=0; i < this.selection.customProperties.length; i++) {
          this.selection.customProperties[i] = [];
        }

        this.currentFilter = this.getCurrentFilterFromSelection();
      },

      // b-tabs
      // ******
      onTabChanged(index) {
        if (index > 0 && this.CompareSwitch) {
          this.CompareSwitch = false;
          this.onCompareSwitchChanged(null);
        }
        this.componentKey += 1;
      },

      // ***********************************************************
      // Public methods
      // ***********************************************************
      getRefName(cust) {
        // console.log('Dashboard - getRefName');

        return cust.name + 'Treeselect';
      },

      closeInfoDialog() {
        // console.log('Dashboard - closeInfoDialog');

        this.$refs.modalTooMuchGroup.hide();
      },

      getWidgetIsActive(widget) {
        if (this.widgetMenuItems && this.widgetMenuItems.length > widget &&
            this.widgetMenuItems[widget].active && this.clientSelectionCompleted) {
          switch (widget) {
            case Widget.TotalScore:
              return this.widgetTotalScoreData != null;
            case Widget.Project:
              return this.widgetCheckStatusData != null;
            case Widget.CheckRevisions:
              return this.widgetCheckRevisionsData != null;
            case Widget.Kpi:
              return this.widgetKPIData != null;
          }
        }

        return false;
      },

      checkGroupmodeTreeselectDisabled(index) {

        let retVal = false;
        let indexIndicator = undefined;

        // only important if group mode is active
        if (this.CompareSwitch) {

          indexIndicator = this.getGroupmodeIndexIndicator();

          // determine whether other options are selected
          retVal = (indexIndicator !== undefined) && (indexIndicator !== index);
        }

        return retVal;
      },

      // ***********************************************************
      // Private methods
      // ***********************************************************
      checkTooMuchSelectionsForGroupmode() {

        // currently we are trying to allow group selections without a limit.
        // if we want to limit it later, please uncomment the code below
        // and check the value for this.maxAllowedGroupSelections

        // let indexIndicator = this.getGroupmodeIndexIndicator();

        // if (indexIndicator === -1) {
        //   // locations
        //   if (this.selection.locations.length > this.maxAllowedGroupSelections) {
        //     this.selection.locations = this.selection.locations.slice(0, this.maxAllowedGroupSelections);
        //     this.$refs.modalTooMuchGroup.show();
        //   }
        // } else {
        //   // custom property
        //   if (this.selection.customProperties[indexIndicator].length > this.maxAllowedGroupSelections) {
        //     this.selection.customProperties[indexIndicator] = this.selection.customProperties[indexIndicator].slice(0, this.maxAllowedGroupSelections);
        //     this.$refs.modalTooMuchGroup.show();
        //   }
        // }
      },

      getGroupmodeIndexIndicator() {
        let indexIndicator = undefined;

        // check if locations are selected
        if (this.selection.locations.length > 0) {
          indexIndicator = -1;
        } else {
          // check if custom properties are selected
          for (let i=0; i < this.selection.customProperties.length; i++) {
            if (this.selection.customProperties[i] && this.selection.customProperties[i].length > 0) {
              indexIndicator = i;
              break;
            }
          }
        }

        return indexIndicator;
      },

      getClientSettings(clientId) {
        return AdministrationService.getClientSettings(clientId).then(response => {
          if (response.data && response.data[0]) {
            const settings = response.data[0];
            this.clientSettings = settings;
            this.percentageNumberOfDecimalPoints = this.clientSettings.PercentageNumberOfDecimalPoints;

            if (this.clientSettings.DashboardTabs) {
              this.dashboardtabs.EnableDashboardGrid = this.clientSettings.DashboardTabs.EnableDashboardGrid;
              this.dashboardtabs.EnableDashboardFunnel = this.clientSettings.DashboardTabs.EnableDashboardFunnel;
              this.dashboardtabs.EnableDashboardRanking = this.clientSettings.DashboardTabs.EnableDashboardRanking;
              this.dashboardtabs.EnableDashboardGeomap = this.clientSettings.DashboardTabs.EnableDashboardGeomap;
              this.dashboardtabs.EnableDashboardScorecard = this.clientSettings.DashboardTabs.EnableDashboardScorecard;
            }
          }
        });
      },

      getDashboardWidgetData() {
        // console.log('Dashboard - getDashboardWidget');

        const token = this.initCancelToken(this.widgetCancelToken);

        if (this.currentFilter.surveys) {

          DashboardService.getDashboardWidgetData(this.currentFilter, token)
            .then(response => {
              this.initializeWidgetData(response.data);
            })
            .catch(error => {
              console.warn('Error to receive dashboard widget data: ' + error);
            });
        } else {
          this.widgetTotalScoreData = null;
          this.widgetLastFiveChecksData = null;
          this.widgetRankingVsLevelData = null;
          this.widgetCheckStatusData = null;
        }
      },

      initializeWidgetData(widgetData) {
        if (!widgetData) { return; }

        // total score
        if (widgetData.TotalScore && widgetData.TotalScore.length > 0) {
          // initialize missing values (no data in DB, 0 checks etc.)
          if (widgetData.TotalScore[0].Result == undefined)
          {
            widgetData.TotalScore[0].Result = null;
          }

          this.widgetTotalScoreData = widgetData.TotalScore[0];
        } else {
          this.widgetTotalScoreData = null;
        }

        // check count
        if (widgetData.Projects && widgetData.Projects.length > 0) {
          // initialize missing values (no data in DB, 0 checks etc.)
          if (widgetData.Projects[0].InProgress == undefined)
          {
            widgetData.Projects[0].InProgress = null;
          }
          if (widgetData.Projects[0].Finished == undefined)
          {
            widgetData.Projects[0].Finished = null;
          }

          this.widgetCheckStatusData = widgetData.Projects[0];
        } else {
          this.widgetCheckStatusData = null;
        }

        this.widgetCheckRevisionsData = widgetData.CheckRevisions;
        this.widgetKPIData = widgetData.KPI;

        this.widgetMenuItems[Widget.TotalScore].active = this.widgetMenuItems[Widget.TotalScore].isEnabled && this.widgetTotalScoreData !== null;
        this.widgetMenuItems[Widget.Project].active = this.widgetCheckStatusData !== null;
        this.widgetMenuItems[Widget.CheckRevisions].active = this.widgetMenuItems[Widget.CheckRevisions].isEnabled && this.widgetCheckRevisionsData !== null;
      },

      initCancelToken(token) {
        // console.log('Dashboard - initCancelToken');

        // Cancel previous request
        if (token)
          token.cancel();
        return axios.CancelToken.source();
      },

      getfilterValues(initialLoad = false) {
        // console.log('Dashboard - getfilterValues');
        this.setAppLoading(true);
        const optionsCancelToken = this.initCancelToken(this.optionsCancelToken);

        DashboardService.getOptions(
          initialLoad ? { client: this.selection.client } : this.getCurrentFilterFromSelection(),
          optionsCancelToken)
        .then(async (response) => {
          if (response.data) {
            //console.log('Dashboard - getfilterValues received data: ' + JSON.stringify(response.data));
            this.setAppLoading(false);
            if (this.selection.client == null) {
              this.selection.client = response.data.clients[0].id;
              await this.getClientSettings(this.selection.client);
            }

            if (initialLoad)
            {
              this.selection.projects = [];
              this.selection.surveys = [];
              this.selection.waves = [];
              this.selection.scenarios = [];
              this.selection.dateRange.startDate = null;
              this.selection.dateRange.endDate = null;
              this.selection.locations = [];
              this.selection.customProperties = [];
            }

            this.setFilterValues(response.data);

            if (!initialLoad && this.canChangeKpiMode)
              this.KpiSwitch = this.KpiSwitch && this.isKpiAvailable;

            if (initialLoad)
            {
              // preselect survey and waves
              if (this.filterValues.projects && this.filterValues.projects.length > 0 &&
                  this.filterValues.projects[0].children && this.filterValues.projects[0].children.length > 0) {

                const surveyPreselection = this.clientSettings?.SurveyPreselection?.filter(surveyId => this.filterValues.surveys.some(s => s.id == surveyId));
                if (!_.isEmpty(surveyPreselection))
                {
                  this.selection.surveys = surveyPreselection.map(item => `S${item}`)
                }
                else if (APP_TARGET === 'audi') {
                  // Should be the Survey with the highest ExtSurveyId
                  let maxExtSurveyId = Math.max(...this.filterValues.surveys.map(x => x.ExternalSurveyIdNumeric));
                  this.selection.surveys = [`S${this.filterValues.surveys.find(x => x.ExternalSurveyIdNumeric === maxExtSurveyId).id}`];
                }
                else if (APP_TARGET === "db") { }
                else {
                  this.selection.surveys = [this.filterValues.projects[0].children[0].id];
                }

                this.selectWaves();
              }
            }

            // Remove invalid client location selections
            if (this.selection.locations && this.selection.locations.length > 0)
              this.selection.locations = this.selection.locations
                .filter(locationId => this.filterValues.locations && this.filterValues.locations.find(l => l.id === locationId));

            // Apply potentially changed selection
            this.currentFilter = this.getCurrentFilterFromSelection();
          }
        })
        .catch(error => {
          this.setAppLoading(false);
          console.warn('Error while receive dashboard filter values: ' + error);
        });
      },

      mapSurveysToWaves(items) {
        return _(items).map(waves => ({
          label: _.first(waves).label,
          id: _.join(waves.map(w => w.id), ','),
          surveyId: _.join(waves.map(w => w.surveyId), ','),
          surveyIds: waves.map(w => w.surveyId),
          WaveDate: _.maxBy(waves, 'WaveDate').WaveDate,
          IsDefault: _.maxBy(waves, 'IsDefault').IsDefault,
          isDisabled: _.minBy(waves, 'isDisabled').isDisabled,
          IsLocalWave: _.first(waves).IsLocalWave
        }))
        .value()
      },

      // Select waves for the current survey selection
      // This is dependent on the current APP_TARGET
      setWaves(filterdata) {
        if (!this.filterValues.waves)
          return;

        if (APP_TARGET === "audi") {
          this.filterValues.waves = [
            {
              id: 'AUDI AG',
              label: 'AUDI AG',
              children: this.mapSurveysToWaves(_.groupBy(filterdata.waves.filter(x => !x.IsLocalWave), 'label'))
            }, {
              id: 'Local Waves',
              label: 'Local Waves',
              children: this.mapSurveysToWaves(_.groupBy(filterdata.waves.filter(x => x.IsLocalWave), 'label')) }
          ];
        } else {
          this.filterValues.waves =
            _(this.filterValues.waves)
              .groupBy('label')
              .map(waves => ({
                  label: _.first(waves).label,
                  id: _.join(waves.map(w => w.id), ','),
                  surveyId: _.join(waves.map(w => w.surveyId), ','),
                  surveyIds: waves.map(w => w.surveyId),
                  WaveDate: _.maxBy(waves, 'WaveDate').WaveDate,
                  IsDefault: _.maxBy(waves, 'IsDefault').IsDefault,
                  isDisabled: _.minBy(waves, 'isDisabled').isDisabled,
                }))
              .value();
        }
      },

      setFilterValues(filterdata) {
        // take it all
        if (filterdata) {
          this.filterValues = filterdata;

          // group waves by label
          this.setWaves(filterdata);

          // group scenarios by label
          if (this.filterValues.scenarios)
            this.filterValues.scenarios =
              _(this.filterValues.scenarios)
                .groupBy('label')
                .map(scenarios => ({
                    id: _.join(scenarios.map(s => s.id), ','),
                    label: _.first(scenarios).label,
                    isDisabled: _.minBy(scenarios, 'isDisabled').isDisabled,
                  }))
                .value();

          if (this.filterValues.checkCounts) {
            const checkCountMap = new Map();
            let maxCheckDate = null;

            for (const entry of this.filterValues.checkCounts)
            {
              if (entry.Date == null) continue;

              const date = moment(entry.Date);
              if (maxCheckDate < date)
                maxCheckDate = date;
              checkCountMap.set(entry.Date, entry.CheckCount);
            }

            this.filterValues.checkCountMap = checkCountMap;
            this.filterValues.maxCheckDate = maxCheckDate;
          }

          // Sort locations by checked state and label
          if (this.filterValues.locations)
            this.filterValues.locations =
              _.orderBy(this.filterValues.locations, l =>
                [ this.selection.locations && this.selection.locations.includes(l.id) ? 0 : 1, l.label ]);

          if (this.filterValues.customProperties)
          {
            // Prepare custom properties for compare mode
            this.filterValues.customPropertiesEnabled = _.cloneDeep(this.filterValues.customProperties);
            this.filterValues.customPropertiesEnabled.forEach(cp => {
              if (cp.children) cp.children.forEach(prop => prop.isDisabled = false)
            });
          }
        }

        // kpi available
        if (this.filterValues.commonFilterSettings && this.filterValues.commonFilterSettings.length > 0 &&
            this.filterValues.commonFilterSettings[0].KpiAvailable != null && this.filterValues.commonFilterSettings[0].KpiAvailable != undefined) {
          this.isKpiAvailable = this.filterValues.commonFilterSettings[0].KpiAvailable;
        }
      },

      filterWavesBySurvey() {
        let items = [];
        if (APP_TARGET === 'audi') {
          this.filterValues.waves.forEach(w => { items.push(...w.children); });
        } else {
          items = this.filterValues.waves;
        }

        return items.filter(w => this.selection.surveys
              .map(s => parseInt(s.substring(1)))
              .some(surveyId => w.surveyIds.includes(surveyId)));
      },

      selectWaves() {
        // console.log('Dashboard - selectWaves');
        //console.log(this.filterValues.waves);
        let selectedWaveIds = [];

        if (this.filterValues.waves && this.filterValues.waves.length > 0 &&
            this.selection.surveys && this.selection.surveys.length > 0) {

          // depends on APP_TARGET
          let filteredWaves = this.filterWavesBySurvey();

          // Preselect default waves
          selectedWaveIds = filteredWaves.filter(w => w.IsDefault).map(w => w.id);

          // if no preselection fits, preselect first wave in list
          if (selectedWaveIds.length === 0 && filteredWaves.length > 0) {
            selectedWaveIds = [filteredWaves[0].id];
          }
        }

        this.selection.waves = selectedWaveIds;
      },

      getCurrentFilterFromSelection() {
        // console.warn('Dashboard - getCurrentFilterFromSelection', JSON.stringify(this.selection));

        let currentFilter = {
          client: this.selection.client,
          projects: null,
          surveys: this.selection.surveys && this.selection.surveys.length > 0 ? this.selection.surveys.map(s => s.substring(1)).join(",") : null,
          waves: this.selection.waves && this.selection.waves.length > 0 ? this.selection.waves.join(",") : null,
          scenarios: this.selection.scenarios && this.selection.scenarios.length > 0 ? this.selection.scenarios.join(",") : null,
          dateFrom: this.selection.dateRange.startDate,
          dateTo: this.selection.dateRange.endDate,
          locations: this.selection.locations && this.selection.locations.length > 0 ? this.selection.locations.join(",") : null,
          customProperties: this.selection.customProperties && this.selection.customProperties.length > 0 ?
            this.selection.customProperties.filter(cp => !_.isEmpty(cp)).map(cp => cp.join('|')).join('|') : null,
          includeCheckCount: this.isDateRangeFilterEnabled == true
        }

        // console.log('Dashboard - getCurrentFilterFromSelection - filter: ' + JSON.stringify(currentFilter));
        return currentFilter;
      },

      checkFilterChanged(filter = null) {
        // console.log('Dashboard - checkFilterChanged');

        let retVal = false;
        let filterToCompare = filter ? filter : this.currentFilter;

        // if no last filter selection available treat it like a filter change
        if (!filterToCompare) {
          return true;
        }

        // check each filter selection
        const currentFilterSelection = this.getCurrentFilterFromSelection();

        retVal = currentFilterSelection.client !== filterToCompare.client;
        retVal = retVal | currentFilterSelection.projects !== filterToCompare.projects;
        retVal = retVal | currentFilterSelection.surveys !== filterToCompare.surveys;
        retVal = retVal | currentFilterSelection.waves !== filterToCompare.waves;
        retVal = retVal | currentFilterSelection.scenarios !== filterToCompare.scenarios;
        retVal = retVal | currentFilterSelection.dateFrom?.getTime() !== filterToCompare.dateFrom?.getTime() ||
          currentFilterSelection.dateTo?.getTime() !== filterToCompare.dateTo?.getTime();
        retVal = retVal | currentFilterSelection.locations !== filterToCompare.locations;
        retVal = retVal | currentFilterSelection.customProperties !== filterToCompare.customProperties;

        return retVal;
      },

      localizationChanged(localizationId) {
        this.getfilterValues(false);
      }
    }
  }

</script>

<style lang="scss" scoped>
:deep(.nav-tabs .nav-link.active) {
  background-color: var(--control-bg-color) !important;
  border-color: var(--bs-border-color);
  border-bottom: 2px solid var(--color-foreground);
  color: var(--color-foreground);
  background-color: var(--bs-primary);
  font-weight: bold;
}

.flex-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.space-left {
  margin-left: 5px;
}

.emptyplacehold{
  width: 100%;
  height: 200%;
  text-align: center;
  margin-top: 7em;
}

.container{
  margin-left: 0px;
  margin-right: 0px;
  padding-left: 0px;
  padding-right: 0px;
  max-width: 100%;
  width: 100%;
}

.card{
  margin-bottom: 20px;
  word-wrap: unset;
}

.card-body{
  min-height: 150px;
}

  selected {
    color: #222222;
  }

  .headinfos{
    display: inline-block;
    width: 100%;
  }

  .bottomline {
    bottom: 0px;
  }

  .navListBorder {
    width: 120px;
    float: right;
    bottom: 0px;
    -webkit-padding-start: 0px;
    border-top: 2px solid #bfbfbf;
    border-left: 2px solid #bfbfbf;
  }

  .navLineBorder {
    float: right;
    bottom: 0px;
    width: 400px;
    -webkit-padding-start: 0px;
    border-top: 2px solid #bfbfbf;
    border-left: 2px solid #bfbfbf;
  }

  nav ul {
    list-style-type: none;
    font-size: 11pt;
    font-weight: bold;
    margin-bottom: 0px;
    margin-top: 6px;
    -webkit-padding-start: 12px;

    color: #bfbfbf;
  }
  .customerlogo{
    float: right;
    right: 0px;
    bottom: 0px;
    margin: 6px;
    margin-top:0px;
    margin-bottom: 7.5px;
  }
  .rightMenu {
    float: right;
    width: auto;
  }

  h3 {
    color: #8fa1bc;
    margin-bottom: 0px;
    font-size: 16pt;
    float: left;
    font-stretch: extra-condensed;
  }

</style>
